// Central content + imagery for the Million Words site.

export const LOGO =
  "https://customer-assets-lxgj4vgw.emergentagent.net/job_learning-xr-platform/artifacts/ni7kh9n7_4.png";

export const NAV_LINKS = [
  { label: "Work", href: "/#work" },
  { label: "Services", href: "/#services" },
  { label: "Studio", href: "/#studio" },
  { label: "Contact", href: "/#contact" },
];

export const HERO_LINES = [
  "A picture is worth",
  "a thousand words.",
  "A video is worth",
  "a million.",
];

export const MARQUEE_ITEMS = [
  "Extended Reality",
  "Digital Learning",
  "Animation & VFX",
  "Simulation",
  "Immersive Training",
  "Video Production",
  "Digital Twins",
  "Voice & Dubbing",
];

export const STATS = [
  { value: "1M+", label: "Words told in motion" },
  { value: "12+", label: "Years crafting media" },
  { value: "40+", label: "Languages localized" },
  { value: "300+", label: "Programs shipped" },
];

export const SERVICES = [
  {
    id: "xr",
    index: "01",
    title: "Extended Reality & Simulation",
    tagline: "Worlds you can step inside.",
    description:
      "Immersive VR, AR and MR experiences, plus high-fidelity simulations and digital twins that turn complex facilities into interactive, explorable spaces.",
    image:
      "https://images.unsplash.com/photo-1593508512255-86ab42a8e620?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    groups: [
      {
        name: "Extended Reality (XR)",
        items: [
          "Virtual Reality (VR) Experiences",
          "Augmented Reality (AR) Experiences",
          "Mixed Reality (MR) Applications",
          "XR Training Solutions",
          "XR Marketing Experiences",
        ],
      },
      {
        name: "Simulation & Digital Twins",
        items: [
          "Simulation Development",
          "3D Simulations",
          "Virtual Replicas of Facilities",
        ],
      },
    ],
  },
  {
    id: "learning",
    index: "02",
    title: "Digital Learning",
    tagline: "Knowledge that actually sticks.",
    description:
      "End-to-end learning — from instructional design and custom eLearning to a homegrown LMS and full production, localization and assessment.",
    image:
      "https://images.pexels.com/photos/3807755/pexels-photo-3807755.jpeg?auto=compress&cs=tinysrgb&w=1400",
    groups: [
      {
        name: "Learning Content Development",
        items: [
          "Custom eLearning Development",
          "Microlearning Modules",
          "Interactive & Immersive Learning",
          "Gamified Learning",
          "Mobile Learning (mLearning)",
          "Instructional Design & Storyboarding",
          "Course Authoring",
        ],
      },
      {
        name: "Learning Management Systems",
        items: [
          "Homegrown LMS Platform",
          "LMS Customization & Integration",
          "LMS Administration",
          "Content Management",
          "Enterprise Learning Platforms",
        ],
      },
      {
        name: "Learning Production",
        items: [
          "Content Digitization",
          "AR/VR Learning Experiences",
          "Educational Videos",
          "Localization into Multiple Languages",
          "Voice-Over & Dubbing",
          "Course Deployment",
          "Evaluation & Learner Assessment",
        ],
      },
    ],
  },
  {
    id: "creative",
    index: "03",
    title: "Creative Services",
    tagline: "Stories, produced beautifully.",
    description:
      "A full creative studio — animation and VFX, audio and media, and cinematic video production for brands, campaigns and training.",
    image:
      "https://images.unsplash.com/photo-1778166226816-9ce348801b2e?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    groups: [
      {
        name: "Animation & VFX",
        items: [
          "2D Animation",
          "3D Animation",
          "Visual Effects (VFX)",
          "Motion Graphics",
          "Video Compositing",
          "Green-Screen Production",
        ],
      },
      {
        name: "Audio & Media",
        items: [
          "Podcast Production",
          "Audio Recording",
          "Voice-Over Production",
          "Dubbing",
          "Sound Editing",
        ],
      },
      {
        name: "Video Production",
        items: [
          "Corporate Videos",
          "Marketing & Promotional Videos",
          "Educational Videos",
          "Training Videos",
          "Event Videos",
        ],
      },
    ],
  },
];

export const MANIFESTO = [
  {
    number: "01",
    title: "Our Mission",
    body:
      "To produce highly successful and deeply loved eLearning videos and programs — content that customers are proud to share and audiences never forget.",
  },
  {
    number: "02",
    title: "Our Values",
    body:
      "Business generated in an ethical manner, in a way that is genuinely beneficial to our customers and to society at large.",
  },
  {
    number: "03",
    title: "Our Vision",
    body:
      "To play a key role in the future of knowledge — accelerating effective, joyful knowledge-sharing across the 21st century.",
  },
];

export const PORTFOLIO = [
  {
    title: "Immersive VR Safety Training",
    category: "Extended Reality",
    slug: "vr-safety-training",
    span: "lg:col-span-7",
    image:
      "https://images.pexels.com/photos/7561900/pexels-photo-7561900.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=760&w=1200",
  },
  {
    title: "AR Product Showcase",
    category: "Augmented Reality",
    slug: "ar-product-showcase",
    span: "lg:col-span-5",
    image:
      "https://images.unsplash.com/photo-1622979135225-d2ba269cf1ac?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  {
    title: "Enterprise Learning Platform",
    category: "Digital Learning",
    slug: "enterprise-learning-platform",
    span: "lg:col-span-5",
    image:
      "https://images.unsplash.com/photo-1525547719571-a2d4ac8945e2?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
  {
    title: "3D Animated Explainer Series",
    category: "Animation & VFX",
    slug: "3d-animated-explainer",
    span: "lg:col-span-7",
    image:
      "https://images.unsplash.com/photo-1600869009498-8d429f88d4f5?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    title: "Corporate Brand Film",
    category: "Video Production",
    slug: "corporate-brand-film",
    span: "lg:col-span-7",
    image:
      "https://images.unsplash.com/photo-1628571201556-82dd32d5398b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  },
  {
    title: "Podcast & Voice-Over Studio",
    category: "Audio & Media",
    slug: "podcast-voiceover-studio",
    span: "lg:col-span-5",
    image:
      "https://images.unsplash.com/photo-1590602847861-f357a9332bbc?crop=entropy&cs=srgb&fm=jpg&q=85&w=1000",
  },
];

export const SERVICE_OPTIONS = [
  "Extended Reality (XR)",
  "Simulation & Digital Twins",
  "Digital Learning / eLearning",
  "Learning Management System",
  "Animation & VFX",
  "Audio & Media",
  "Video Production",
  "Something else",
];

export const CONTACT = {
  email: "inquiries@millionwords.com",
  phone: "+1 (919) 659-5495",
  phoneHref: "tel:+19196595495",
};
